import React from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Avatar,
  Progress,
  Tag,
  Spacer,
  Divider,
} from "@chakra-ui/react";
import { CustomDrawer } from "@root/components/common/drawer";
import { useTranslation } from "react-i18next";
import { CloseIcon } from "@chakra-ui/icons";
import { ExcelIcon } from "@root/assets/icons/ExcelIcon";
import { NoEnrolledIcon } from "@root/assets/icons/NoEnrolledIcon";
import { COMPLETED, IN_PROGRESS, NOT_STARTED } from "@root/webConstants";

const enrolledUsers = [
  {
    name: "Olivia Rhye",
    avatar: "https://randomuser.me/api/portraits/women/1.jpg",
    status: "inProgress",
    completion: 40,
    certification: false,
  },
  {
    name: "Olivia Rhye",
    avatar: "https://randomuser.me/api/portraits/women/1.jpg",
    status: "completed",
    completion: 100,
    certification: true,
  },
  {
    name: "Olivia Rhye",
    avatar: "https://randomuser.me/api/portraits/women/1.jpg",
    status: "inProgress",
    completion: 40,
    certification: false,
  },
  {
    name: "Olivia Rhye",
    avatar: "https://randomuser.me/api/portraits/women/1.jpg",
    status: "inProgress",
    completion: 40,
    certification: false,
  },
  {
    name: "Olivia Rhye",
    avatar: "https://randomuser.me/api/portraits/women/1.jpg",
    status: "completed",
    completion: 100,
    certification: true,
  },
  {
    name: "Liam Chen",
    avatar: "https://randomuser.me/api/portraits/men/2.jpg",
    status: "completed",
    completion: 100,
    certification: false,
  },
  {
    name: "Emma Patel",
    avatar: "https://randomuser.me/api/portraits/women/3.jpg",
    status: "notStarted",
    completion: 0,
    certification: false,
  },
];

const statusTag = (status: string, t: any) => {
  let bg, color, label;

  switch (status) {
    case COMPLETED:
      bg = "#ECFDF3";
      color = "green";
      label = t("courseDrawer.completed");
      break;
    case IN_PROGRESS:
      bg = "#FFFAEB";
      color = "orange";
      label = t("courseDrawer.inProgress");
      break;
    case NOT_STARTED:
      color = "gray";
      label = t("courseDrawer.notStarted");
      break;
    default:
      return null;
  }

  return (
    <Tag size="sm" bg={bg} color={color} borderRadius="full" py={1}>
      ● {label}
    </Tag>
  );
};

type CourseDrawerProps = {
  open: boolean;
  onClose: () => void;
  course?: { title: string; completion: number };
};

const SubscriberDrawer = ({
  open,
  onClose,
  course = { title: "Media Ethics", completion: 50 },
}: CourseDrawerProps) => {
  const { t } = useTranslation("common");
  return (
    <CustomDrawer
      isHeader={false}
      open={open}
      onClose={onClose}
      title={course?.title}
    >
      <Box p={6}>
        <Flex align="center" justifyContent={"space-between"} mb={8}>
          <Flex>
            <Text fontWeight="bold" fontSize="2xl">
              {course?.title || "Media Ethics"}
            </Text>
            <Tag
              ml={4}
              bg={"#3538CD"}
              color={"white"}
              borderRadius="full"
              fontWeight={600}
              fontSize="sm"
            >
              {course?.completion}% {t("courseDrawer.completed")}
            </Tag>
          </Flex>
          <Box cursor={"pointer"} onClick={onClose}>
            Close
            <CloseIcon ml={2} />
          </Box>
        </Flex>
        <Flex gap={2} mb={6} align={"center"}>
          <Spacer />
          <Button
            border={"1px solid #1819B4"}
            borderRadius="8px"
            color="#1819B4"
            variant="outline"
            height={"36px"}
            fontWeight={500}
          >
            <ExcelIcon />
            <Box ml={2}> {t("courseDrawer.downloadCSV")} </Box>
          </Button>
        </Flex>
        <Divider mb={4} />
        {/* Enrolled users */}
        <Text color={"#A7B2BA"} fontWeight={600} mb={2}>
          {t("courseDrawer.enrolledUsers")}
        </Text>
        <Box maxH="55vh" overflowY="auto">
          {enrolledUsers.map((user, idx) => (
            <Flex
              key={idx}
              align="center"
              mb={4}
              p={3}
              borderRadius="16px"
              border={"1px solid #F0EEFF"}
            >
              <Avatar name={user.name} src={user.avatar} size="md" mr={4} />
              <Box flex={1} minW={0}>
                <Flex
                  align="center"
                  justifyContent={"space-between"}
                  gap={2}
                  mb={1}
                >
                  <Text fontWeight={500} isTruncated>
                    {user.name}
                  </Text>
                  <Flex>
                    {user.certification && (
                      <Tag
                        mr={2}
                        size="sm"
                        bg="#EEF4FF"
                        color="blue"
                        borderRadius="full"
                      >
                        {t("courseDrawer.certificationAwarded")}
                      </Tag>
                    )}
                    {statusTag(user.status, t)}
                  </Flex>
                </Flex>
                <Flex alignItems={"center"} justifyContent={"space-between"}>
                  <Progress
                    value={user?.completion}
                    size="sm"
                    colorScheme={user.completion === 100 ? "green" : "blue"}
                    borderRadius="8px"
                    w="80%"
                    bg="#E4EBF0"
                    mb={1}
                  />
                  <Text fontSize="sm" color="gray.500">
                    {user?.completion}% {t("courseDrawer.completed")}
                  </Text>
                </Flex>
              </Box>
            </Flex>
          ))}
        </Box>

        {/* No Enrolled Users found */}
        {!enrolledUsers?.length && (
          <Flex
            flexDir={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            height={"700px"}
          >
            <NoEnrolledIcon />
            <Text fontWeight={600} fontSize={"24px"} mt={7}>
              {t("courseDrawer.noEnrolledJournalist")}
            </Text>
            <Text fontWeight={400} fontSize={"18px"} mt={1}>
              {t("courseDrawer.noEnrolledCourses")}
            </Text>
          </Flex>
        )}
      </Box>
    </CustomDrawer>
  );
};

export default SubscriberDrawer;
